import { mdiInformationOutline } from '@mdi/js'
import Icon from '@mdi/react'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import { ChangeEvent, MouseEvent, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import styles from './AiContentCheckbox.pcss'

interface AiContentCheckboxProps {
  checked: boolean
  /** `data-testid` for the control - bulk rows must suffix it with the post id so it stays unique. */
  id?: string
  className?: string
  /** Renders the control as a switch instead of a checkbox. */
  variant?: 'checkbox' | 'switch'
  /** Replaces the default "AI content" label and its info tooltip. */
  label?: ReactNode
  onChange: (value: boolean) => void
}

/** Shared "this post is AI-generated" toggle - used by the composer media section and the bulk post options. */
export function AiContentCheckbox(props: AiContentCheckboxProps) {
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    props.onChange(e.target.checked)
  }

  // The icon sits inside the <label>, so a click on it would toggle the checkbox
  const preventToggle = (e: MouseEvent) => {
    e.preventDefault()
  }

  const label = props.label ? (
    <span className={styles['label-content']}>{props.label}</span>
  ) : (
    <span className={styles['label-content']}>
      <FormattedMessage id="composer.labels.ai-content" />
      {props.variant !== 'switch' && (
        <Tooltip title={<FormattedMessage id="composer.labels.ai-content.info" />} describeChild>
          <span className={styles.info} data-testid="ai-content-info" onClick={preventToggle}>
            <Icon path={mdiInformationOutline} size="16px" />
          </span>
        </Tooltip>
      )}
    </span>
  )

  return (
    <FormControlLabel
      data-testid={props.id}
      label={label}
      classes={{
        label: `${styles.label} ${props.variant === 'switch' ? styles['label-switch'] : ''}`,
        root: `${styles.root} ${props.className || ''}`
      }}
      control={props.variant === 'switch' ? (
        <Switch
          checked={props.checked}
          color="primary"
          size="small"
          className={styles.switch}
          // EXPL: MUI renders the input as a plain checkbox - role="switch" matches what it looks like
          slotProps={{ input: { role: 'switch' } }}
          onChange={onChange}
        />
      ) : (
        <Checkbox
          checked={props.checked}
          color="primary"
          size="small"
          className={styles.cb}
          onChange={onChange}
        />
      )}
    />
  )
}
